import {
  Avatar,
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Typography,
} from "@material-ui/core";
import { Theme, makeStyles } from "@material-ui/core/styles";
import { ArrowBack, CheckCircle } from "@material-ui/icons";
import { User } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { useStateValue } from "../../config/AppContext";
import Icons from "../Utilities/Icons";
import SfC from "../Utilities/SearchFilter";

const useStyles = makeStyles((theme: Theme) => ({
  drawer: {
    "& .MuiDrawer-paper": {
      width: 512,
      backgroundColor: "#f3f3f3",
      "& .drawer-heading": {
        height: 75,
        backgroundColor: "var(--text-primary)",
        color: "#ffffff",
        fontSize: 20,
        fontWeight: 600,
        padding: "25px 15px",
        "& h5": {
          fontSize: 20,
          fontWeight: 500,
        },
        "& svg": {
          cursor: "pointer",
          marginRight: 15,
        },
      },
      "& .drawer-body": {
        overflow: "auto",
        height: "85vh",
        padding: 15,
        "& #search-filter": {
          backgroundColor: "#ffffff",
          marginTop: 0,
          top: -15,
          padding: "inherit",
        },
        "& .client-head": {
          width: "100%",
          height: 35,
          padding: 5,
          border: "1px solid #e5e5e5",
          color: "var(--text-primary)",
          fontSize: 14,
          fontWeight: 600,
        },
        "& .client-item": {
          cursor: "pointer",
          position: "relative",
          width: "100%",
          height: 80,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid var(--heading)",
          padding: "20px 5px",
          "& .status": {
            float: "right",
            fontSize: theme.spacing(2),
            fontWeight: 500,
            color: "#ffffff",
            "& svg": {
              marginRight: 15,
              height: theme.spacing(4),
              width: theme.spacing(4),
              fill: "var(--text-heading)",
            },
          },
          "&.active": {
            "& .status svg": {
              fill: "var(--text-primary)",
            },
          },
          "&:hover": {
            backgroundColor: "var(--heading)",
            color: "#ffffff",
          },
          "& p": {
            display: "inline-block",
            width: "60%",
            "& span": {
              display: "block",
            },
          },
        },
        "& .MuiBadge-root": {
          marginLeft: 15,
          marginRight: 15,
          marginTop: -10,
          "& .MuiBadge-dot": {
            height: 11,
            width: 11,
          },
          "&.REGISTERED .MuiBadge-dot": {
            backgroundColor: "#7ed321",
          },
          "&.INVITED .MuiBadge-dot": {
            backgroundColor: "#ffb319",
          },
          "& .MuiAvatar-root": {
            backgroundColor: "var(--heading)",
          },
        },
        "& .MuiBadge-anchorOriginTopRightRectangle": {
          top: 5,
          right: 10,
        },
      },
      "& .drawer-action": {
        paddingLeft: 20,
        paddingRight: 20,
        backgroundColor: "#ffffff",
        "& button": {
          height: 50,
          backgroundColor: "var(--text-primary)",
          color: "#ffffff",
          fontSize: theme.spacing(2),
          textTransform: "none",
          "&:hover": {
            backgroundColor: "var(--text-primary)",
          },
        },
      },
    },
  },
  dialog: {
    "& .MuiDialog-paper": {
      position: "absolute",
      width: "100%",
      margin: 0,
      top: 0,
      bottom: 0,
    },
  },
  header: {
    height: 75,
    backgroundColor: "var(--text-primary)",
    color: "#ffffff",
    fontSize: 20,
    fontWeight: 500,
    padding: "25px 15px",
    "& h2": {
      fontSize: 20,
      fontWeight: 500,
    },
    "& svg": {
      cursor: "pointer",
      marginRight: 15,
    },
  },
  body: {
    overflow: "auto",
    height: "85vh",
    padding: 15,
    "& #search-filter": {
      backgroundColor: "#ffffff",
      top: -15,
      marginTop: 0,
      padding: "inherit",
    },
    "& .client-head": {
      width: "100%",
      height: 35,
      padding: 5,
      border: "1px solid #e5e5e5",
      color: "var(--text-primary)",
      fontSize: 14,
      fontWeight: 600,
    },
    "& .client-item": {
      cursor: "pointer",
      position: "relative",
      width: "100%",
      height: 80,
      backgroundColor: "#ffffff",
      borderBottom: "1px solid var(--heading)",
      padding: "20px 5px",
      "& .status": {
        float: "right",
        fontSize: theme.spacing(2),
        fontWeight: 500,
        color: "#ffffff",
        "& svg": {
          marginRight: 15,
          height: theme.spacing(4),
          width: theme.spacing(4),
          fill: "var(--text-heading)",
        },
      },
      "&.active": {
        "& .status svg": {
          fill: "var(--text-primary)",
        },
      },
      "&:hover": {
        backgroundColor: "var(--heading)",
        color: "#ffffff",
      },
      "& p": {
        display: "inline-block",
        width: "60%",
        "& span": {
          display: "block",
        },
      },
    },
    "& .MuiBadge-root": {
      marginLeft: 15,
      marginRight: 15,
      marginTop: -10,
      "& .MuiBadge-dot": {
        height: 11,
        width: 11,
      },
      "&.REGISTERED .MuiBadge-dot": {
        backgroundColor: "#7ed321",
      },
      "&.INVITED .MuiBadge-dot": {
        backgroundColor: "#ffb319",
      },
      "& .MuiAvatar-root": {
        backgroundColor: "var(--heading)",
      },
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      top: 5,
      right: 10,
    },
  },
  action: {
    paddingLeft: 20,
    paddingRight: 20,
    backgroundColor: "#ffffff",
    "& button": {
      height: 50,
      backgroundColor: "var(--text-primary)",
      color: "#ffffff",
      fontSize: theme.spacing(2),
      textTransform: "none",
      "&:hover": {
        backgroundColor: "var(--text-primary)",
      },
    },
  },
}));

type FilteredProps = User.IUserClient[] | null;
type SelectedProps = User.IUserClient["id"][];
type SelectClientProps = {
  show: boolean;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onHide: () => void;
  single?: boolean;
  selected?: string[];
  [x: string]: any;
};

export default function SelectClientsModal(props: SelectClientProps) {
  const classes = useStyles();
  const { clients } = useStateValue() as { clients: User.IUserClient[] };
  const { ic_user } = Icons;
  const [filtered, setFiltered] = React.useState<FilteredProps>(null);
  const [selected, setSelected] = React.useState<SelectedProps>(
    props.selected ? props.selected : []
  );

  // Generate individual client item
  const generateListItem = (client: User.IUserClient, i: number) => {
    return (
      <li
        key={i}
        id={client.id}
        className={
          selected && selected.find((el) => el === client.id) ? "client-item active" : "client-item"
        }
        onClick={handleSelect}
      >
        <Badge variant="dot" className={client.reg_status ? client.reg_status : ""}>
          <Avatar alt="profile" src={client.photo ? client.photo.medium : ic_user} />
        </Badge>
        <Typography variant="body1" noWrap>
          {`${client.first_name} ${client.last_name}`}
        </Typography>
        <span className="status">
          <CheckCircle />
        </span>
      </li>
    );
  };
  // Generate listing with alphabetical headers
  const generateHeadList = (accum: Array<any>, client: any, i: number) => {
    const head = client.last_name && client.last_name[0].toUpperCase();
    if (accum.find((el: any) => el.props.id === head.toUpperCase())) {
      accum = [...accum, generateListItem(client, i)];
      return accum;
    }
    accum = [
      ...accum,
      <Typography variant="h5" id={head} key={uuid()} className="client-head">
        {head.toUpperCase()}
      </Typography>,
      generateListItem(client, i),
    ];
    return accum;
  };
  // Search / Filter handler
  const handleFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.value === "") return setFiltered(null);
    const filter = new RegExp(e.currentTarget.value.trim(), "i");
    const filteredClients = clients.filter(
      (client) =>
        filter.test(client.first_name) ||
        filter.test(client.last_name) ||
        filter.test(`${client.email}`) ||
        filter.test(`${client.first_name} ${client.last_name}`) ||
        filter.test(`${client.last_name} ${client.first_name}`)
    );
    return setFiltered(filteredClients);
  };
  // Select handler
  const handleSelect = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    props.single
      ? selected.length > 0 && selected.find((el) => el === e.currentTarget.id)
        ? setSelected(selected.filter((el) => el !== e.currentTarget.id))
        : setSelected([e.currentTarget.id])
      : !selected
      ? setSelected([e.currentTarget.id])
      : selected.find((el) => el === e.currentTarget.id)
      ? setSelected(selected.filter((el) => el !== e.currentTarget.id))
      : setSelected([...selected, e.currentTarget.id]);
  };
  const { t } = useTranslation();
  return window.innerWidth < 700 ? (
    <Dialog id="SelectClients" open={props.show} onClose={props.onHide} className={classes.dialog}>
      <DialogTitle className={classes.header}>
        <ArrowBack
          onClick={() => {
            setFiltered(null);
            return props.onHide();
          }}
        />
        {t("Client Selection")}
      </DialogTitle>
      <DialogContent className={classes.body}>
        <form
          id="select-clients"
          onSubmit={(e) => {
            setFiltered(null);
            return props.onSubmit(e);
          }}
        >
          <input
            id="clients"
            name="clients"
            style={{ display: "none" }}
            value={JSON.stringify(selected)}
            readOnly
          />
          <SfC placeholder="clients" onChange={handleFilter} />
          <ul className="client-listing">
            {filtered
              ? filtered.reduce(generateHeadList, [])
              : clients.reduce(generateHeadList, [])}
          </ul>
        </form>
      </DialogContent>
      <DialogActions className={classes.action}>
        <Button fullWidth type="submit" form="select-clients" variant="contained">
          {t("Done ")}
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <Drawer anchor="right" open={props.show} onClose={props.onHide} className={classes.drawer}>
      <div className="drawer-heading">
        <Typography variant="h5">
          <ArrowBack
            onClick={() => {
              setFiltered(null);
              return props.onHide();
            }}
          />
          {t("Client Selection")}
        </Typography>
      </div>
      <div className="drawer-body">
        <form
          id="select-clients"
          onSubmit={(e) => {
            setFiltered(null);
            return props.onSubmit(e);
          }}
        >
          <input
            id="clients"
            name="clients"
            style={{ display: "none" }}
            value={JSON.stringify(selected)}
            readOnly
          />
          <SfC placeholder="clients" onChange={handleFilter} />
          <ul className="client-listing">
            {filtered
              ? filtered.reduce(generateHeadList, [])
              : clients.reduce(generateHeadList, [])}
          </ul>
        </form>
      </div>
      <div className="drawer-action">
        <Button fullWidth type="submit" form="select-clients" variant="contained">
          {t("Done ")}
        </Button>
      </div>
    </Drawer>
  );
}
